/**
 * AI Lock Store
 *
 * Tracks which tabs are currently locked by AI for editing.
 * Updated by IPC events from the main process.
 */

import { create } from 'zustand';

/** Lock info for a tab */
export type AiLockInfo = {
  sessionId: string;
  tabId: string;
  tabName?: string;
  lockedAt: string;
  lastActivity?: string;
  /** Status message from AI (e.g., "Refactoring query...") */
  message?: string;
  /** Whether user has requested to abort */
  abortRequested?: boolean;
  /** Whether this is a new-style per-tab meta lock */
  isMetaLock?: boolean;
};

interface AiLockState {
  /** Map of tabId -> lock info */
  lockedTabs: Map<string, AiLockInfo>;

  /** Set a lock on a tab */
  setLock: (tabId: string, lockInfo: AiLockInfo) => void;
  /** Remove a lock from a tab */
  removeLock: (tabId: string) => void;
  /** Remove all locks for a session */
  removeSessionLocks: (sessionId: string) => void;
  /** Check if a tab is locked */
  isLocked: (tabId: string) => boolean;
  /** Get lock info for a tab */
  getLockInfo: (tabId: string) => AiLockInfo | undefined;
  /** Update last activity timestamp for a lock */
  touchLock: (tabId: string, activityAt: string) => void;
  /** Update lock message */
  updateLockMessage: (tabId: string, message?: string) => void;
  /** Set abort requested flag */
  setAbortRequested: (tabId: string, requested: boolean) => void;
}

export const useAiLockStore = create<AiLockState>((set, get) => ({
  lockedTabs: new Map(),

  setLock: (tabId, lockInfo) => {
    set((state) => {
      const newMap = new Map(state.lockedTabs);
      newMap.set(tabId, lockInfo);
      return { lockedTabs: newMap };
    });
  },

  removeLock: (tabId) => {
    set((state) => {
      const newMap = new Map(state.lockedTabs);
      newMap.delete(tabId);
      return { lockedTabs: newMap };
    });
  },

  removeSessionLocks: (sessionId) => {
    set((state) => {
      const newMap = new Map(state.lockedTabs);
      for (const [tabId, lock] of newMap) {
        if (lock.sessionId === sessionId) {
          newMap.delete(tabId);
        }
      }
      return { lockedTabs: newMap };
    });
  },

  isLocked: (tabId) => {
    return get().lockedTabs.has(tabId);
  },

  getLockInfo: (tabId) => {
    return get().lockedTabs.get(tabId);
  },

  touchLock: (tabId, activityAt) => {
    set((state) => {
      const current = state.lockedTabs.get(tabId);
      if (!current) return state;

      const newMap = new Map(state.lockedTabs);
      newMap.set(tabId, { ...current, lastActivity: activityAt });
      return { lockedTabs: newMap };
    });
  },

  updateLockMessage: (tabId, message) => {
    set((state) => {
      const current = state.lockedTabs.get(tabId);
      if (!current) return state;

      const newMap = new Map(state.lockedTabs);
      newMap.set(tabId, { ...current, message });
      return { lockedTabs: newMap };
    });
  },

  setAbortRequested: (tabId, requested) => {
    set((state) => {
      const current = state.lockedTabs.get(tabId);
      if (!current) return state;

      const newMap = new Map(state.lockedTabs);
      newMap.set(tabId, { ...current, abortRequested: requested });
      return { lockedTabs: newMap };
    });
  },
}));
